import { Component, computed, effect, inject } from '@angular/core';
import {
    VoucherDetailPrescriptionsMedicalProceduresStore,
} from './voucher-detail-prescriptions-medical-procedures.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { VoucherTypeEnum } from '@libs/types/src/common/enum/voucher-type.enum';
import { VoucherDetailService } from '../../../pages/person/voucher-detail-page/voucher-detail-page.service';
import {
    VoucherPrescribedMedicalProcedureObject,
} from '@libs/types/src/services/object-type/voucher/voucher-prescribed-medical-procedure.object';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import {
    ServiceMedicalProcedureObject,
} from '@libs/types/src/services/object-type/service/service-medical-procedure.object';
import { FilterBuilder } from '../../../helper/filter.builder';
import { getServiceOperationalCapacityFrag } from './voucher-detail-prescriptions-medical-procedures.query';
import {
    ServiceOperationalCapacityUnitEnum,
} from '@libs/types/src/services/enum/service-operational-capacity-unit.enum';
import { AppFormGroup, changeSignal, enableFormControls } from '../../../../../utils/form.utils';
import {
    WEEKEND_CODES,
} from '../../../../../components/medical-service/medical-procedure/medical-procedure-select/medical-procedure-select.component';
import { listAllFreeDaysInBetween } from '@libs/types/src/utils/date.utils';
import { contentGlobalWarnings } from '../../../content.error';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { FormatServiceProcedurePipe } from '../../../../shared/pipes/format-service-procedure.pipe';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { FormControl, FormsModule } from '@angular/forms';
import { InputWysiwygComponent } from '../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { Select } from '@openng/optimus-ui/select';
import { InputText } from '@openng/optimus-ui/inputtext';
import { TranslatePipe } from '@ngx-translate/core';
import { Dialog } from '@openng/optimus-ui/dialog';
import { StripAndShowComponent } from '../../../../shared/components/kit/strip-and-show/strip-and-show.component';
import { sanitizeHtmlInput } from '@libs/shared/utils/sanitizer';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import {
    TransformSingleVoucherPrescriptionPipe,
    transformSingleVoucherProcedure,
} from '../../../../shared/components/kit/performed-procedures/performed-procedures.pipes';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TableColumnType } from '../../../../shared/components/kit/table/table.types';

@Component({
    selector: 'app-voucher-detail-prescriptions-medical-procedures',
    templateUrl: './voucher-detail-prescriptions-medical-procedures.component.html',
    styleUrls: [
        './voucher-detail-prescriptions-medical-procedures.component.scss',
    ],
    providers: [VoucherDetailPrescriptionsMedicalProceduresStore],
    imports: [
        SelectInputComponent,
        FormatServiceProcedurePipe,
        TableComponent,
        Checkbox,
        FormsModule,
        InputWysiwygComponent,
        Select,
        InputText,
        TranslatePipe,
        Dialog,
        StripAndShowComponent,
        TransformSingleVoucherPrescriptionPipe,
        PrimeTemplate,
    ],
})
export class VoucherDetailPrescriptionsMedicalProceduresComponent extends SubscriptionComponent {

    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(VoucherDetailPrescriptionsMedicalProceduresStore);
    private readonly detailService = inject(VoucherDetailService);
    private readonly confirm = inject(ConfirmBuilder);


    protected voucherId: number | null = null;
    protected voucherType: VoucherTypeEnum = this.detailService.getVoucherType();

    protected form: AppFormGroup = this.detailService.prescriptionsForm;
    protected formProcedureCodeChanged = changeSignal<ServiceMedicalProcedureObject>(this.form.controls['medicalProcedureCode'] as any);
    protected formPrescriptionsChanged = changeSignal<VoucherPrescribedMedicalProcedureObject[]>(
        this.form.controls['prescriptions'] as FormControl<VoucherPrescribedMedicalProcedureObject[]>,
    );
    protected prescriptionItems = computed(() => (this.formPrescriptionsChanged() || []).map((item) => Object.assign(item, {
        __hideDelete: !item.canBeDeleted,
    })));

    protected cannotDeleteWarningVisible = false;

    protected usualNumberOfApplicationsOptions: { name: number; value: number }[] = [];

    protected columns = computed(() => {
        return [
            {
                name: 'code',
                type: TableColumnType.PerformedProcedures,
                dataKey: null,
                data: (item: VoucherPrescribedMedicalProcedureObject) => [transformSingleVoucherProcedure(item)],
            },
            {
                name: 'description',
            },
            { name: 'canBeBilled' },
            ...this.voucherType == VoucherTypeEnum.ORP
                ? [
                    { name: 'canBeBilledWeekend' },
                    { name: 'canBeBilledNight' },
                ]
                : [],
            ...this.voucherType == VoucherTypeEnum.ORP && this.store.isContinuous()
                ? [
                    { name: 'continuousOperation' },
                ]
                : [],
            {
                name: 'courseDescription',
            },
            {
                name: 'recordTemplate',
            },
            {
                name: 'usualNumberOfApplications',
            },
            {
                name: 'frequency',
            },
        ];
    });

    constructor() {
        super();
        for (let i = 1; i <= 7; i++) {
            this.usualNumberOfApplicationsOptions.push({ name: i, value: i });
        }

        effect(() => {
            const form: any = this.detailService.generalForm;
            const filter = new FilterBuilder();
            filter.limit(1);
            filter.serviceId(this.store.serviceId());
            filter.where('dateFrom', (form.value.issueDate || new Date()).toISOString(), 'LTE');
            filter.where('name', ServiceOperationalCapacityUnitEnum.CONTINOUS_OPERATION_WORK_SCHEDULE);
            filter.where('dateTo', (form.value.closureDate || form.value.validTo || new Date()).toISOString(), 'GTE');
            filter.orWhere('dateTo', '', 'EMPTY');

            this.store.api.getServiceOperationalCapacities(
                filter.filter(),
                getServiceOperationalCapacityFrag,
            ).then();

            enableFormControls(this.form, this.folderStore.canEdit());
        });

        effect(() => {
            const actProcedureCode = this.formProcedureCodeChanged();
            this.prescribeMedicalProcedure(actProcedureCode);
        });
    }

    protected prescribeMedicalProcedure(procedure: ServiceMedicalProcedureObject): void {
        if (!procedure) return;

        const id = procedure.id;
        if (
            id &&
            !this.form?.value?.prescriptions?.find(
                (it: VoucherPrescribedMedicalProcedureObject) =>
                    it.serviceMedicalProcedureId == id,
            )
        ) {
            let frequency: string | null = null;
            if (WEEKEND_CODES.includes(procedure.code)) {
                const from: Date | null = this.detailService.generalForm.value.issueDate;
                const to: Date | null = this.detailService.generalForm.value.closureDate || this.detailService.generalForm.value.validTo;
                frequency = listAllFreeDaysInBetween(from, to).join(', ');
            }

            this.form.patchValue({
                prescriptions: [
                    ...(this.form.value.prescriptions || []),
                    {
                        code: procedure.code,
                        canBeBilled: true,
                        serviceMedicalProcedure: procedure,
                        serviceMedicalProcedureId: procedure.id,
                        frequency: procedure.frequency || frequency || null,
                        courseDescription: procedure.courseDescription ? sanitizeHtmlInput(procedure.courseDescription) : null,
                        recordTemplate: procedure.textTemplate ? sanitizeHtmlInput(procedure.textTemplate) : null,
                        usualNumberOfApplications: 1,
                        canBeBilledWeekend: true,
                        canBeBilledNight: true,
                        canBeDeleted: true,
                    } as any,
                ],
                medicalProcedureCode: null,
            }, { isCodeUpdate: true });
        } else {
            contentGlobalWarnings.next('voucher.messages.medicalProcedureExists');
            this.form.patchValue({ medicalProcedureCode: null }, { isCodeUpdate: true });
        }
    }

    protected openDeleteConfirmation(input: any): void {
        this.confirm.confirm(
            'database.vouchers.prescriptions.deleteProcedureConfirmation.header',
            'database.vouchers.prescriptions.deleteProcedureConfirmation.label',
            () => {
                this.detailService.patchPrescriptionsForm({
                    prescriptions: this.form.value.prescriptions.filter(
                        (it: VoucherPrescribedMedicalProcedureObject) => {
                            return (
                                it.serviceMedicalProcedureId !=
                                input.item.serviceMedicalProcedureId
                            );
                        },
                    ),
                });
            },
            null,
            'pi-trash',
            input.event.target,
        );
    }

    protected editField(
        value: string | number,
        id: keyof Pick<VoucherPrescribedMedicalProcedureObject, 'courseDescription' | 'recordTemplate' | 'frequency' | 'usualNumberOfApplications'>,
        item: VoucherPrescribedMedicalProcedureObject,
    ) {
        const prescriptions = this.form.value.prescriptions || [];
        this.form.patchValue({
            prescriptions: prescriptions.map((it: VoucherPrescribedMedicalProcedureObject) => {
                if (it.serviceMedicalProcedureId == item.serviceMedicalProcedureId) {
                    return {
                        ...it,
                        [id]: value,
                    };
                }
                return it;
            }),
        }, { isCodeUpdate: true });
    }

}
